import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Pagination,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { locationService, LocationSubmission, AdminReviewRequest } from '../services/locationService';

const LocationSubmissions: React.FC = () => {
  const [submissions, setSubmissions] = useState<LocationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<LocationSubmission | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | 'request_revision'>('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // Details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    loadSubmissions();
  }, [page, statusFilter]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const response = await locationService.getLocationSubmissions({
        status: statusFilter || undefined,
        page,
        page_size: 20
      });
      
      setSubmissions(response.submissions);
      setTotalPages(response.total_pages);
      setError(null);
    } catch (err) {
      setError('Failed to load location submissions');
      console.error('Error loading submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmission = (submission: LocationSubmission) => {
    setSelectedSubmission(submission);
    setReviewAction('approve');
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleViewDetails = async (submission: LocationSubmission) => {
    try {
      const response = await locationService.getSubmissionDetails(submission.id);
      setSelectedSubmission(response.submission);
      setDetailsDialogOpen(true);
    } catch (err) {
      setError('Failed to load submission details');
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedSubmission) return;

    try {
      setReviewLoading(true);
      
      const reviewRequest: AdminReviewRequest = {
        action: reviewAction,
        notes: reviewNotes || undefined
      };

      await locationService.reviewSubmission(selectedSubmission.id, reviewRequest);
      
      setReviewDialogOpen(false);
      setSelectedSubmission(null);
      setReviewNotes('');
      
      // Reload submissions
      await loadSubmissions();
      
    } catch (err) {
      setError('Failed to submit review');
      console.error('Error submitting review:', err);
    } finally {
      setReviewLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'needs_revision': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && submissions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Location Submissions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="needs_revision">Needs Revision</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                onClick={() => {
                  setPage(1);
                  loadSubmissions();
                }}
                disabled={loading}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Submissions Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted By</TableCell>
                <TableCell>Submitted Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((submission) => (
                <TableRow key={submission.id}>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {submission.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {submission.proposed_address.city}, {submission.proposed_address.state}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {submission.category && (
                      <Chip label={submission.category} size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={submission.status}
                      color={getStatusColor(submission.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{submission.submitted_by}</TableCell>
                  <TableCell>{formatDate(submission.created_at)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(submission)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {submission.status === 'pending' && (
                        <Tooltip title="Review">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleReviewSubmission(submission)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box display="flex" justifyContent="center" p={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Review Location Submission</DialogTitle>
        <DialogContent>
          {selectedSubmission && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedSubmission.name}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {selectedSubmission.description}
              </Typography>
              
              <Box mt={2}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={reviewAction}
                    label="Action"
                    onChange={(e) => setReviewAction(e.target.value as any)}
                  >
                    <MenuItem value="approve">Approve</MenuItem>
                    <MenuItem value="reject">Reject</MenuItem>
                    <MenuItem value="request_revision">Request Revision</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Review Notes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  margin="normal"
                  placeholder="Add notes about your decision..."
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            disabled={reviewLoading}
          >
            {reviewLoading ? <CircularProgress size={20} /> : 'Submit Review'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Submission Details</DialogTitle>
        <DialogContent>
          {selectedSubmission && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">{selectedSubmission.name}</Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {selectedSubmission.description}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Address</Typography>
                  <Typography variant="body2">
                    {selectedSubmission.proposed_address.formatted_address ||
                     `${selectedSubmission.proposed_address.street}, ${selectedSubmission.proposed_address.city}, ${selectedSubmission.proposed_address.state}`}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Coordinates</Typography>
                  <Typography variant="body2">
                    {selectedSubmission.coordinates.latitude}, {selectedSubmission.coordinates.longitude}
                  </Typography>
                </Grid>
                
                {selectedSubmission.contact_info && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Contact Information</Typography>
                    {selectedSubmission.contact_info.phone && (
                      <Typography variant="body2">Phone: {selectedSubmission.contact_info.phone}</Typography>
                    )}
                    {selectedSubmission.contact_info.email && (
                      <Typography variant="body2">Email: {selectedSubmission.contact_info.email}</Typography>
                    )}
                    {selectedSubmission.contact_info.website && (
                      <Typography variant="body2">Website: {selectedSubmission.contact_info.website}</Typography>
                    )}
                  </Grid>
                )}
                
                {selectedSubmission.tags.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Tags</Typography>
                    <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                      {selectedSubmission.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                    </Box>
                  </Grid>
                )}
                
                {selectedSubmission.submission_notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Submitter Notes</Typography>
                    <Typography variant="body2">{selectedSubmission.submission_notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationSubmissions;